import { apiFetch } from '../utils/apiFetch.js';

const DEFAULT_LEAVE_ERROR = 'Could not leave this Battle Room. The room remains unchanged. Check your connection and try again.';
const LEAVE_TIMEOUT_MS = 20000;

/**
 * Read the server's structured membership error without exposing raw transport
 * or database details to the player.
 * date_of_change: 2026-08-24
 */
const readLeaveError = async (response) => {
  try {
    const payload = await response.json();
    const serverError = payload?.error;
    const message = typeof serverError === 'string' ? serverError : serverError?.message;
    const code = typeof serverError === 'object' ? serverError?.code : null;
    return {
      code: code || 'SESSION_LEAVE_FAILED',
      message: String(message || DEFAULT_LEAVE_ERROR),
    };
  } catch (_error) {
    return { code: 'SESSION_LEAVE_FAILED', message: DEFAULT_LEAVE_ERROR };
  }
};

/**
 * Leave or dismiss one Battle Room only after the authoritative service
 * confirms the membership mutation. Callers must keep local room state intact
 * when this promise rejects.
 * date_of_change: 2026-08-24
 */
export const leaveCombatSessionMembership = async (sessionId) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), LEAVE_TIMEOUT_MS);
  let response;
  try {
    response = await apiFetch(`/api/combat/${sessionId}/leave`, {
      method: 'POST',
      requireAuth: true,
      signal: controller.signal,
    });
  } catch (cause) {
    const timedOut = cause?.name === 'AbortError' || controller.signal.aborted;
    const error = new Error(timedOut
      ? 'The Battle Room service did not confirm Leave within 20 seconds. The room remains unchanged in this view; check the connection before trying again.'
      : DEFAULT_LEAVE_ERROR);
    error.code = timedOut ? 'SESSION_LEAVE_TIMEOUT' : 'SESSION_LEAVE_NETWORK_ERROR';
    throw error;
  } finally {
    clearTimeout(timeout);
  }

  if (!response.ok) {
    const detail = await readLeaveError(response);
    const error = new Error(detail.message);
    error.code = detail.code;
    error.status = response.status;
    throw error;
  }

  return response.json();
};

export default leaveCombatSessionMembership;
